import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/pages/LegalPage";
import { LEGAL_OWNER } from "@/lib/legal";

const TOC = [
  { id: "responsable", label: "Responsable y contacto" },
  { id: "datos", label: "Datos que tratamos" },
  { id: "pagos", label: "Datos de pago" },
  { id: "finalidades", label: "Finalidades" },
  { id: "comunicaciones", label: "Transaccionales y marketing" },
  { id: "proveedores", label: "Proveedores utilizados" },
  { id: "transferencias", label: "Transferencias internacionales" },
  { id: "seguridad", label: "Seguridad y control de acceso" },
  { id: "conservacion", label: "Conservación" },
  { id: "menores", label: "Menores de edad" },
  { id: "cookies", label: "Cookies y almacenamiento local" },
  { id: "derechos", label: "Tus derechos" },
  { id: "cambios", label: "Cambios y vigencia" },
];

export const Route = createFileRoute("/privacidad")({
  head: () => ({
    meta: [
      { title: "Política de privacidad — Yustiz Haro Resources" },
      {
        name: "description",
        content:
          "Qué datos trata Yustiz Haro Resources, con qué finalidad, qué proveedores intervienen y cómo ejercer tus derechos de acceso, rectificación y supresión.",
      },
      { property: "og:title", content: "Política de privacidad — Yustiz Haro Resources" },
      {
        property: "og:description",
        content: "Tratamiento de datos personales, proveedores y derechos del titular.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recursos.yustizharo.com/privacidad" },
    ],
    links: [{ rel: "canonical", href: "https://recursos.yustizharo.com/privacidad" }],
  }),
  component: PrivacidadPage,
});

function PrivacidadPage() {
  return (
    <LegalPage
      title="Política de privacidad"
      currentPath="/privacidad"
      toc={TOC}
      intro={
        <p>
          Esta política describe cómo {LEGAL_OWNER.brand} trata los datos personales de quienes
          visitan, se registran o compran en {LEGAL_OWNER.site}, conforme a la Ley 25.326 de
          Protección de los Datos Personales de la {LEGAL_OWNER.law}.
        </p>
      }
    >
      <h2 id="responsable">1. Responsable y contacto</h2>
      <p>
        Responsable: {LEGAL_OWNER.holder}, CUIT {LEGAL_OWNER.cuit}, con domicilio en{" "}
        {LEGAL_OWNER.address}, que opera bajo el nombre comercial {LEGAL_OWNER.brand}. Canal de
        contacto en materia de datos personales:{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>.
      </p>

      <h2 id="datos">2. Datos que tratamos</h2>
      <h3>Datos de registro y cuenta</h3>
      <ul>
        <li>Nombre y dirección de email.</li>
        <li>
          Cuando ingresás con Google: email, nombre y foto de perfil básica provistos por esa
          cuenta, junto con el identificador del proveedor de autenticación.
        </li>
      </ul>
      <h3>Datos de compra</h3>
      <ul>
        <li>Productos adquiridos, importes, moneda (ARS o USD) y fecha de la operación.</li>
        <li>Estado del pago, identificador de pedido e identificador de la operación del procesador.</li>
      </ul>
      <h3>Biblioteca y descargas</h3>
      <ul>
        <li>Historial de compras y permisos de acceso a cada producto.</li>
        <li>Registros de descarga: qué archivo se descargó y cuándo.</li>
      </ul>
      <h3>Newsletter y soporte</h3>
      <ul>
        <li>
          Email, nombre opcional, intereses seleccionados y constancia del consentimiento cuando
          te suscribís a novedades.
        </li>
        <li>
          Consultas de soporte y solicitudes enviadas desde los formularios de contacto y de
          arrepentimiento: nombre, email, identificador de pedido, motivo y mensaje.
        </li>
      </ul>
      <h3>Datos técnicos</h3>
      <ul>
        <li>
          Dirección IP, tipo de navegador y dispositivo, y registros técnicos y de seguridad
          generados por el servidor y la plataforma de backend.
        </li>
        <li>
          Preferencias guardadas en el almacenamiento local de tu navegador: región seleccionada
          (Argentina o Internacional), idioma y contenido del carrito. Esa información permanece
          en tu dispositivo y no se envía a terceros con fines publicitarios.
        </li>
      </ul>

      <h2 id="pagos">3. Datos de pago</h2>
      <p>
        Los datos completos de tarjetas, cuentas y credenciales de pago se ingresan y procesan
        íntegramente en los entornos de Mercado Pago y PayPal. {LEGAL_OWNER.brand} no los recibe
        ni los almacena: solo conserva el resultado de la operación (estado, importe, moneda e
        identificador) para acreditar la compra y habilitar el acceso.
      </p>

      <h2 id="finalidades">4. Finalidades de cada tratamiento</h2>
      <ul>
        <li>
          <strong>Cuenta y autenticación:</strong> identificarte, permitir el ingreso y vincular
          tus compras a tu email.
        </li>
        <li>
          <strong>Compras y pagos:</strong> registrar el pedido, verificar el pago, emitir
          comprobantes y cumplir obligaciones legales y fiscales.
        </li>
        <li>
          <strong>Entrega y biblioteca:</strong> habilitar el acceso a los archivos, generar
          enlaces de descarga firmados y temporales, y permitir re-descargas.
        </li>
        <li>
          <strong>Registros de descarga:</strong> prevenir el uso indebido y resolver incidencias
          técnicas.
        </li>
        <li>
          <strong>Soporte y reembolsos:</strong> atender consultas, verificar pedidos y tramitar
          solicitudes.
        </li>
        <li>
          <strong>Newsletter:</strong> enviarte novedades y contenidos cuando lo consentiste
          expresamente.
        </li>
        <li>
          <strong>Seguridad:</strong> detectar fraude, abuso y accesos no autorizados.
        </li>
      </ul>

      <h2 id="comunicaciones">5. Comunicaciones transaccionales y de marketing</h2>
      <p>
        Las comunicaciones transaccionales (confirmación de compra, entrega de accesos,
        recuperación de contraseña, enlaces de acceso y avisos sobre tu pedido) son necesarias
        para ejecutar la relación contractual y se envían siempre que corresponda.
      </p>
      <p>
        Las comunicaciones comerciales o de marketing son opcionales, específicas y revocables:
        solo se envían si te suscribiste voluntariamente y podés darte de baja en cualquier
        momento desde el enlace incluido en cada envío o escribiendo a{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>. Retirar el consentimiento
        comercial no afecta tu acceso a las compras ni a las comunicaciones transaccionales.
      </p>

      <h2 id="proveedores">6. Proveedores que intervienen</h2>
      <ul>
        <li>
          <strong>Lovable Cloud (infraestructura Supabase):</strong> alojamiento del sitio, base
          de datos, autenticación y almacenamiento privado de los archivos.
        </li>
        <li>
          <strong>Google:</strong> autenticación mediante inicio de sesión con Google, cuando
          elegís esa opción.
        </li>
        <li>
          <strong>Mercado Pago:</strong> procesamiento de pagos en pesos argentinos.
        </li>
        <li>
          <strong>PayPal:</strong> procesamiento de pagos en dólares estadounidenses.
        </li>
        <li>
          <strong>Servicio de email de Lovable:</strong> envío de correos transaccionales y de
          autenticación desde el dominio notify.recursos.yustizharo.com, con registro del estado
          de entrega de cada envío.
        </li>
      </ul>
      <p>
        No se utilizan servicios de analítica ni píxeles publicitarios: Google Analytics 4, Meta
        Pixel y herramientas equivalentes no están activos en este sitio. Si en el futuro se
        incorporara alguno, se solicitará tu consentimiento previo y granular, con la posibilidad
        de rechazarlo con la misma facilidad con la que se acepta, y esta política se actualizará
        antes de su activación.
      </p>

      <h2 id="transferencias">7. Transferencias internacionales</h2>
      <p>
        Algunos de los proveedores mencionados alojan o procesan datos fuera de la Argentina, en
        particular en los Estados Unidos y en la Unión Europea. Esas transferencias se realizan
        para ejecutar la relación contractual (alojamiento, autenticación, procesamiento de pagos
        y envío de correos), amparadas en las excepciones previstas por el artículo 12 de la Ley
        25.326 y en los compromisos contractuales de confidencialidad y seguridad asumidos por
        cada proveedor.
      </p>

      <h2 id="seguridad">8. Seguridad y control de acceso</h2>
      <p>
        Se aplican medidas técnicas y organizativas razonables: cifrado en tránsito, control de
        acceso por roles, políticas de seguridad a nivel de fila en la base de datos,
        almacenamiento privado de los archivos y enlaces de descarga firmados con vencimiento
        breve. El acceso administrativo está limitado a las cuentas autorizadas del titular.
      </p>

      <h2 id="conservacion">9. Conservación de los datos</h2>
      <p>
        Los datos de compra y facturación se conservan durante los plazos exigidos por la
        normativa fiscal y comercial argentina. Los datos de cuenta y biblioteca se conservan
        mientras la cuenta esté activa y sea necesaria para dar acceso a lo adquirido. Las
        consultas de soporte se conservan mientras sean útiles para resolver el caso y acreditar
        su tratamiento. Los datos de newsletter se conservan hasta que retires el consentimiento.
      </p>

      <h2 id="menores">10. Menores de edad</h2>
      <p>
        El sitio está dirigido a personas mayores de 18 años o a menores autorizados por sus
        representantes legales. No se recopilan de forma consciente datos de menores sin esa
        autorización; si se detecta un caso, los datos se eliminan.
      </p>

      <h2 id="cookies">11. Cookies y almacenamiento local</h2>
      <p>
        El sitio utiliza únicamente almacenamiento técnico necesario: la cookie o el token de
        sesión que mantiene tu inicio de sesión, y el almacenamiento local del navegador donde se
        guardan tu región, tu idioma y tu carrito. No se utilizan cookies publicitarias ni de
        seguimiento de terceros, por lo que no se muestra un banner de consentimiento que no
        sería necesario. Podés borrar estos datos desde la configuración de tu navegador, con la
        consecuencia de perder la sesión y el carrito guardado.
      </p>

      <h2 id="derechos">12. Tus derechos</h2>
      <p>
        Podés ejercer los derechos de acceso, rectificación, actualización y supresión de tus
        datos personales, así como retirar el consentimiento comercial y darte de baja de las
        novedades, escribiendo a{" "}
        <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a> o mediante la{" "}
        <Link to="/contacto">página de contacto</Link>.
      </p>
      <p>
        La solicitud de acceso se responde dentro de los 10 días corridos de su recepción. Las
        solicitudes de rectificación, actualización o supresión se atienden dentro de los 5 días
        hábiles. Para verificar tu identidad puede pedirse información adicional relacionada con
        la compra.
      </p>
      <p>
        La Agencia de Acceso a la Información Pública, órgano de control de la Ley 25.326, tiene
        la atribución de atender las denuncias y reclamos que interpongan quienes resulten
        afectados en sus derechos por incumplimiento de las normas vigentes en materia de
        protección de datos personales.
      </p>

      <h2 id="cambios">13. Cambios y vigencia</h2>
      <p>
        Esta política puede actualizarse por cambios legales, técnicos u operativos. La versión
        vigente es la publicada en esta página, con la fecha de actualización indicada arriba. Si
        el cambio afectara de forma sustancial el tratamiento de tus datos, se comunicará al email
        registrado.
      </p>
    </LegalPage>
  );
}
